import { dirhams, operationsCount, present, TYPE_LABELS } from '@horof/shared';
import { Alert, App, Input, Modal, Radio } from 'antd';
import { useState } from 'react';
import { useClient } from 'urql';
import {
  bulkError,
  financeContext,
  type Lists,
  type TableOperation,
  UpdateOperationsDocument,
} from './data';
import type { Draft } from './OperationsScreen';
import { CategoryPicker, CounterpartyPicker, ProjectPicker } from './pickers';

type Field = 'categoryId' | 'counterpartyId' | 'projectId' | 'review';

const FIELDS: { value: Field; label: string }[] = [
  { value: 'categoryId', label: 'الصنف' },
  { value: 'counterpartyId', label: 'الجهة' },
  { value: 'projectId', label: 'المشروع' },
  { value: 'review', label: 'المراجعة' },
];

/**
 * «تغيير المحدَّد»: one field set on all the selected operations, in one
 * all-or-nothing call (arqam doc 56). The summary lists what each row has
 * now; if arqam refuses one row, it is named and nothing is changed.
 */
export function BulkChange({
  rows,
  lists,
  onDone,
  onClose,
}: {
  rows: TableOperation[];
  lists: Lists | undefined;
  onDone: () => void;
  onClose: () => void;
}) {
  const client = useClient();
  const { message } = App.useApp();
  const [field, setField] = useState<Field>('categoryId');
  const [value, setValue] = useState<string | null>(null);
  const [flag, setFlag] = useState(true);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<{
    message: string;
    operationId: string | null;
  } | null>(null);

  // One type selected: its categories and counterparties; mixed: all of them,
  // and arqam names a row whose type the category doesn't fit.
  const types = new Set(rows.map((r) => r.type));
  const type = types.size === 1 ? rows[0].type : null;

  const patch: Draft | null =
    field === 'review'
      ? flag
        ? note.trim()
          ? { needsReview: true, reviewNote: note.trim() }
          : null
        : { needsReview: false, reviewNote: null }
      : field === 'projectId'
        ? value
          ? { projectId: value }
          : null
        : { [field]: value };

  const name = (pool: ({ id: string; name: string } | null)[] | undefined) =>
    present(pool).find((x) => x.id === value)?.name ?? '—';
  const after =
    field === 'categoryId'
      ? name(lists?.operationCategories?.nodes)
      : field === 'counterpartyId'
        ? name(lists?.counterparties?.nodes)
        : field === 'projectId'
          ? name(lists?.projects?.nodes)
          : flag
            ? `تحتاج مراجعة: ${note.trim() || '…'}`
            : 'بلا علامة مراجعة';
  const before = (op: TableOperation): string =>
    field === 'categoryId'
      ? (op.category?.name ?? '—')
      : field === 'counterpartyId'
        ? (op.counterparty?.name ?? '—')
        : field === 'projectId'
          ? (op.project?.name ?? '—')
          : op.needsReview
            ? `تحتاج مراجعة${op.reviewNote ? `: ${op.reviewNote}` : ''}`
            : 'بلا علامة مراجعة';

  async function save() {
    if (!patch) return;
    setSaving(true);
    const result = await client
      .mutation(
        UpdateOperationsDocument,
        { ids: rows.map((r) => r.id), patch },
        financeContext,
      )
      .toPromise();
    setSaving(false);
    if (result.error) {
      const { message: text, operationId, label } = bulkError(result.error);
      setFailure({
        message: label ? `${label}: ${text}` : text,
        operationId,
      });
      return;
    }
    message.success(
      `تغيّرت ${operationsCount(result.data?.appUpdateFinancialOperations?.result ?? rows.length)}`,
    );
    onDone();
  }

  const pick = (f: Field) => {
    setField(f);
    setValue(null);
    setFailure(null);
  };

  return (
    <Modal
      open
      width={760}
      title={`تغيير المحدَّد: ${operationsCount(rows.length)}`}
      okText={`تغيير ${operationsCount(rows.length)}`}
      cancelText="رجوع"
      confirmLoading={saving}
      okButtonProps={{ disabled: !patch }}
      onOk={save}
      onCancel={onClose}
    >
      <div className="desk-bulk">
        <Radio.Group
          optionType="button"
          buttonStyle="solid"
          value={field}
          onChange={(e) => pick(e.target.value)}
          options={FIELDS}
        />
        {field === 'categoryId' && (
          <CategoryPicker
            type={type}
            value={value}
            onChange={setValue}
            placeholder="بلا صنف"
          />
        )}
        {field === 'counterpartyId' && (
          <CounterpartyPicker
            type={type}
            value={value}
            onChange={setValue}
            placeholder="بلا جهة"
          />
        )}
        {field === 'projectId' && (
          <ProjectPicker
            value={value}
            onChange={setValue}
            placeholder="اختر المشروع"
          />
        )}
        {field === 'review' && (
          <>
            <Radio.Group
              value={flag}
              onChange={(e) => setFlag(e.target.value)}
              options={[
                { value: true, label: 'تحتاج مراجعة' },
                { value: false, label: 'إزالة العلامة (رُوجعت)' },
              ]}
            />
            {flag && (
              <Input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="ما الذي يجب مراجعته؟"
                maxLength={300}
              />
            )}
          </>
        )}
        {field === 'categoryId' && types.size > 1 && (
          <div className="field-hint">
            في الاختيار مداخيل ومصاريف: الصنف يجب أن يناسب النوعين.
          </div>
        )}
        {(field === 'categoryId' || field === 'counterpartyId') && !value && (
          <div className="field-hint">
            بلا اختيار: يُفرَّغ الحقل في كل العمليات.
          </div>
        )}
      </div>

      {failure && (
        <Alert
          type="error"
          showIcon
          message="لم يتغيّر شيء."
          description={failure.message}
        />
      )}

      <table className="desk-changes">
        <thead>
          <tr>
            <th>العملية</th>
            <th>قبل</th>
            <th>بعد</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((op) => (
            <tr
              key={op.id}
              className={
                failure?.operationId === op.id ? 'is-failed' : undefined
              }
            >
              <td>
                <div>{op.description || TYPE_LABELS[op.type]}</div>
                <div className="field-hint ltr-num">
                  {op.date} · {dirhams(op.amount)}
                </div>
              </td>
              <td className="is-before">{before(op)}</td>
              <td className="is-after">{patch ? after : '…'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Modal>
  );
}
